// Scenery for the built-in maps: decoration boxes (trees, pools, towers, columns under the platforms)
// and theme helpers, kept clear of the course. The generator is in builtin.ts.

import type { Look, Scenery } from './builtin';
import type { Shape } from './shapes';
import type { LevelTheme, MaterialName, Vec3Tuple } from './types';

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/* ---------------- Scenery helpers ---------------- */

/** Scatter `count` things around the level, clear of the path. */
export function scatter(
  s: Scenery,
  count: number,
  clear: number,
  place: (x: number, y: number, i: number) => void,
) {
  const { min, max } = s.bounds;
  let placed = 0;
  for (let tries = 0; tries < count * 20 && placed < count; tries++) {
    const x = lerp(min[0] - 3000, max[0] + 3000, s.rand());
    const y = lerp(min[1] - 3000, max[1] + 3000, s.rand());
    if (!s.clearOf(x, y, clear)) continue;
    place(x, y, placed);
    placed++;
  }
}

/** Support columns under the platforms down into the void. */
export function columns(
  s: Scenery,
  material: MaterialName,
  color: string,
  depth: number,
  shape: Shape = 'box',
) {
  for (const p of s.platforms)
    s.add([p[0] - 40, p[1] - 40, p[2] - depth], [p[0] + 40, p[1] + 40, p[2] - 64], material, color, shape);
}

/** Below the course, at least `below` units under its lowest point. */
export const floorZ = (s: Scenery, below: number) => s.bounds.min[2] - below;

export function trees(s: Scenery, n: number, trunk: string, leaves: string, leafMat: MaterialName = 'grass') {
  scatter(s, n, 700, (x, y) => {
    const c = 180 + s.rand() * 140;
    const crownTop = floorZ(s, 250 + s.rand() * 400);
    const trunkTop = crownTop - c * 1.3 + 40;
    const base = trunkTop - 400 - s.rand() * 600;
    s.add([x - 40, y - 40, base], [x + 40, y + 40, trunkTop], 'planks', trunk, 'cylinder');
    s.add(
      [x - c, y - c, trunkTop - 40],
      [x + c, y + c, crownTop],
      leafMat,
      leaves,
      s.rand() < 0.5 ? 'oct' : 'box',
    );
  });
}

/** Flat pools at the bottom; each at its own height so they never z-fight. */
export function pools(s: Scenery, n: number, material: MaterialName, color: string, below: number) {
  scatter(s, n, 900, (x, y, i) => {
    const w = 500 + s.rand() * 700;
    const z = floorZ(s, below) + i * 3;
    s.add([x - w, y - w, z - 40], [x + w, y + w, z], material, color, s.rand() < 0.5 ? 'cylinder' : 'oct');
  });
}

export function towers(s: Scenery, n: number, looks: Look[], below: number, shape: Shape = 'box') {
  scatter(s, n, 650, (x, y) => {
    const base = floorZ(s, below + 1500);
    const w = 120 + s.rand() * 220;
    const top = floorZ(s, Math.max(below, 300) + s.rand() * 1200);
    const look = looks[Math.floor(s.rand() * looks.length)];
    s.add([x - w, y - w, base], [x + w, y + w, top], look[0], look[1], shape);
  });
}

export const T = (o: Partial<LevelTheme>): LevelTheme => ({
  skyTop: '#3a79c9',
  skyHorizon: '#bfe3ff',
  fog: '#bfe3ff',
  fogNear: 3000,
  fogFar: 16000,
  sun: '#ffffff',
  sunStrength: 1,
  ambient: 0.6,
  voidColor: '#4f8a3a',
  ...o,
});

/** Where scenery models can go: the generator checks each one against the course. */
export interface Decor {
  rand(): number;
  /** Points you stand on along the course, in order. */
  path: readonly Vec3Tuple[];
  bounds: { min: Vec3Tuple; max: Vec3Tuple };
  /** The floor far below the course (where the void floor is drawn). */
  floor: number;
  /** Place a model with its lowest point at z; false (and nothing placed) when it would come near the course. */
  prop(model: string, x: number, y: number, z: number, scale: number, yaw?: number): boolean;
  /** Start, checkpoint and end platforms: centre, top, size. */
  platforms: readonly { x: number; y: number; z: number; sx: number; sy: number }[];
  /** A small model standing on a platform (below eye height, away from the pieces next to it). */
  platformProp(model: string, x: number, y: number, z: number, scale: number, yaw?: number): boolean;
  /** A floating island (scenery, no collision) beside the course; false when it would come near it. */
  island(x: number, y: number, top: number, r: number, thick: number, look: Look, shape?: Shape): boolean;
}

/** Small models in the corners of the platforms, up to `each` per platform. */
export function platformProps(d: Decor, models: readonly string[], scale: [number, number], each = 3) {
  if (!models.length) return;
  for (const p of d.platforms) {
    const corners = [
      [1, 1],
      [1, -1],
      [-1, 1],
      [-1, -1],
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ];
    // Corners first (shuffled), then the middles of the sides if a corner is too near the next piece.
    for (let i = 3; i > 0; i--) {
      const j = Math.floor(d.rand() * (i + 1));
      [corners[i], corners[j]] = [corners[j], corners[i]];
    }
    let n = 0;
    for (const [cx, cy] of corners) {
      if (n >= each) break;
      const sc = lerp(scale[0], scale[1], d.rand());
      const m = models[Math.floor(d.rand() * models.length)];
      const x = p.x + cx * (p.sx / 2 - 40);
      const y = p.y + cy * (p.sy / 2 - 40);
      if (d.platformProp(m, x, y, p.z, sc, d.rand() * Math.PI * 2)) n++;
    }
  }
}

/**
 * Floating islands beside the course at its height, each with a few models on top: trees, rocks,
 * ruins, whatever the theme has. They keep well clear of the way.
 */
export function islands(
  d: Decor,
  count: number,
  look: Look,
  models: readonly string[],
  scale: [number, number],
  shape: Shape = 'oct',
) {
  const p = d.path;
  if (p.length < 3 || !models.length) return;
  let made = 0;
  for (let tries = 0; tries < count * 15 && made < count; tries++) {
    const i = 1 + Math.floor(d.rand() * (p.length - 2));
    const dx = p[i + 1][0] - p[i - 1][0];
    const dy = p[i + 1][1] - p[i - 1][1];
    const l = Math.hypot(dx, dy) || 1;
    const side = d.rand() < 0.5 ? 1 : -1;
    const r = 240 + d.rand() * 280;
    const off = r + 560 + d.rand() * 900;
    const x = p[i][0] - (dy / l) * off * side;
    const y = p[i][1] + (dx / l) * off * side;
    const top = p[i][2] + (d.rand() - 0.5) * 320;
    if (!d.island(x, y, top, r, 90 + d.rand() * 70, look, shape)) continue;
    made++;
    const n = 1 + Math.floor(d.rand() * 3);
    for (let k = 0; k < n; k++) {
      const a = d.rand() * Math.PI * 2;
      const rr = r * 0.55 * Math.sqrt(d.rand());
      const m = models[Math.floor(d.rand() * models.length)];
      d.prop(
        m,
        x + Math.cos(a) * rr,
        y + Math.sin(a) * rr,
        top,
        lerp(scale[0], scale[1], d.rand()),
        d.rand() * Math.PI * 2,
      );
    }
  }
}

/** Scatter `count` models picked from `models` round the course, on the floor (or at `z`). */
export function scatterProps(
  d: Decor,
  models: readonly string[],
  count: number,
  scale: [number, number],
  spread = 2600,
  z?: number,
) {
  if (!models.length) return;
  const { min, max } = d.bounds;
  let placed = 0;
  for (let tries = 0; tries < count * 12 && placed < count; tries++) {
    const x = lerp(min[0] - spread, max[0] + spread, d.rand());
    const y = lerp(min[1] - spread, max[1] + spread, d.rand());
    const m = models[Math.floor(d.rand() * models.length)];
    const sc = lerp(scale[0], scale[1], d.rand());
    if (d.prop(m, x, y, z ?? d.floor, sc, d.rand() * Math.PI * 2)) placed++;
  }
}

/** Models lining the course on both sides (on the floor), every `every` points along it. */
export function lineProps(
  d: Decor,
  models: readonly string[],
  every: number,
  offset: number,
  scale: [number, number],
) {
  if (!models.length) return;
  const p = d.path;
  for (let i = 1; i + 1 < p.length; i += every) {
    const dx = p[i + 1][0] - p[i - 1][0];
    const dy = p[i + 1][1] - p[i - 1][1];
    const l = Math.hypot(dx, dy) || 1;
    for (const side of [1, -1]) {
      const o = offset * (0.8 + d.rand() * 0.6);
      const x = p[i][0] - (dy / l) * o * side;
      const y = p[i][1] + (dx / l) * o * side;
      const m = models[Math.floor(d.rand() * models.length)];
      d.prop(m, x, y, d.floor, lerp(scale[0], scale[1], d.rand()), d.rand() * Math.PI * 2);
    }
  }
}
